import { Flex, message, PaginationProps, Space, Typography } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import './AlarmPage.scss';
import AlarmTable, { Alarm } from '../../components/alarm/AlarmTable';
import { alertApi } from '../../api/alert.api';
import { locationApi } from '../../api/location.api';
import { installationStateApi } from '../../api/installation-state.api';
import { userApi } from '../../api/user.api';
import { installationStateLabel } from '../../utils/mapTranslationEnums';
import useTenantStore from '../../stores/tenant.store';
import { ADAPTIVE_TABLE_CONTAINER_CLASS, useAdaptivePageSize } from '../../hooks/useAdaptivePageSize';

const { Title } = Typography;
const FIRST_PAGE = 1;

const AlarmPage = () => {
  const { t } = useTranslation('alarms');
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(FIRST_PAGE);
  const [total, setTotal] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const tenants = useTenantStore((state) => state.tenants);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const { pageSize, containerRef, containerHeight, recalculate } = useAdaptivePageSize();

  const [prevSelectedTenant, setPrevSelectedTenant] = useState(selectedTenant);
  if (selectedTenant !== prevSelectedTenant) {
    setPrevSelectedTenant(selectedTenant);
    setPage(FIRST_PAGE);
  }

  const fetchAlarms = useCallback(
    async (pageIndex: number, size: number) => {
      setLoading(true);
      try {
        const response = await alertApi.getAlertByPagination(pageIndex, size);
        const items = await Promise.all(
          response.installations.map(async (installation) => {
            const [location, state] = await Promise.all([
              locationApi.getByUuid(installation.locationUuid).catch(() => null),
              installation.installationStateUuid
                ? installationStateApi.getByUuid(installation.installationStateUuid).catch(() => null)
                : Promise.resolve(null),
            ]);
            const admin = location?.adminUuid
              ? await userApi.getUserByUuid(location.adminUuid).catch(() => null)
              : null;
            const tenant = tenants.find((tenant) => tenant.value === location?.tenantUuid);
            return {
              id: installation.uuid,
              statusAlarm: 'true',
              installation: installation.erpId,
              address: location?.address ?? '',
              admin: admin?.name ?? '-',
              tenant: tenant?.label ?? '-',
              status: installationStateLabel(state),
              statusColor: state?.color ?? '#cccccc',
            };
          }),
        );
        setAlarms(items);
        setTotal(response.metadata?.totalCount ?? 0);
      } catch (error) {
        messageApi.error(t('common:errors.generic'));
      } finally {
        setLoading(false);
      }
    },
    [tenants, messageApi, t],
  );

  useEffect(() => {
    setPage(FIRST_PAGE);
  }, [pageSize]);

  useEffect(() => {
    fetchAlarms(page, pageSize);
  }, [page, pageSize, selectedTenant, fetchAlarms]);

  useLayoutEffect(() => {
    recalculate();
  }, [total, recalculate]);

  const handleTableChange = (pagination: PaginationProps) => {
    const newPage = pagination.current ?? FIRST_PAGE;
    setPage(newPage);
  };

  return (
    <Flex vertical>
      {contextHolder}
      <Space className={'AlarmPage__row'}>
        <Space size={'middle'}>
          <BellOutlined className={'AlarmPage__icon'} />
          <Title level={1} className={'AlarmPage__title'}>
            {t('title')}
          </Title>
        </Space>
      </Space>
      <div
        className={`AlarmPage__table ${ADAPTIVE_TABLE_CONTAINER_CLASS}`}
        style={{ height: containerHeight }}
        ref={containerRef}
      >
        <AlarmTable
          alarms={alarms}
          pagination={{ pageSize, total, current: page, showSizeChanger: false }}
          onChange={handleTableChange}
          loading={loading}
        />
      </div>
    </Flex>
  );
};

export default AlarmPage;
